import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Badge,
  Button,
} from "react-bootstrap";
import { toast } from "react-toastify";
import userAdminService from "../../services/userAdminService";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const u = await userAdminService.get(id);
        setUser(u || null);
      } catch (e) {
        toast.error("Failed to load user details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleBack = () => navigate("/admin/users");
  const handleEdit = () => navigate(`/admin/users/${id}/edit`);

  if (loading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>User not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Users
          </Button>
        </div>
      </Container>
    );
  }

  const fullName =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "-";
  const createdDate = user?.created_at
    ? formatDateToDDMMYYYY(user.created_at)
    : "-";
  const updatedDate = user?.updated_at
    ? formatDateToDDMMYYYY(user.updated_at)
    : "-";

  return (
    <Container className="view-form">
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">User Details</h5>
            <div className="d-flex gap-2">
              <Button size="sm" variant="secondary" onClick={handleBack}>
                Back
              </Button>
              <Button size="sm" variant="warning" onClick={handleEdit}>
                Edit
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              <Row>
                <Col lg={12} className="d-flex align-items-center gap-3">
                  <Row>
                    <Col lg={2} className="bg-light rounded-4">
                      <Row>
                        <Col lg={12} className="my-2">
                          <div className="image-box">
                            <img
                              src={
                                user?.image_url && user.image_url.trim() !== ""
                                  ? user.image_url
                                  : "/not-img-found.png"
                              }
                              alt="user"
                              style={{
                                width: "170px",
                                height: "170px",
                                objectFit: "cover",
                                borderRadius: "10%",
                                boxShadow:
                                  "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                                cursor: "pointer",
                                display: "block",
                                margin: "0 auto",
                                marginTop: "5px",
                              }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = "/not-img-found.png";
                              }}
                            />
                          </div>
                        </Col>
                        <Col lg={12} className="my-2 mt-4 text-center">
                          <label className="fw-bold">
                            <h6>Full Name</h6>
                          </label>
                          <h5
                            className="mb-0"
                            style={{
                              textAlign: "center",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "20px",
                            }}
                          >
                            {fullName}
                          </h5>
                        </Col>
                        <Col lg={12} className="my-2 text-center">
                          <label className="fw-bold">
                            <h6>Status</h6>
                          </label>
                          <h5
                            className="mb-0"
                            style={{
                              textAlign: "center",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "20px",
                            }}
                          >
                            <Badge
                              bg={user?.is_active ? "success" : "secondary"}
                            >
                              {user?.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </h5>
                        </Col>
                        <Col lg={12} className="my-2 text-center">
                          <label className="fw-bold">
                            <h6>User Type</h6>
                          </label>
                          <h5
                            className="mb-0"
                            style={{
                              textAlign: "center",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "20px",
                            }}
                          >
                            <Badge bg="info">
                              {(user?.user_type || "-")
                                .toString()
                                .replace(/_/g, " ")}
                            </Badge>
                          </h5>
                        </Col>
                      </Row>
                    </Col>

                    <Col lg={10}>
                      <Row className="my-2 view-form">
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">First Name</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {user?.first_name || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Last Name</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {user?.last_name || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Email</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {user?.email || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Phone</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {user?.phone || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Created</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {createdDate}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Last Updated</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {updatedDate}
                          </p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserDetails;
